import { apiKeySelector } from 'store/appSettings/selectors';
import * as Config from 'modules/utils/ConfigUtils';
import axios from 'axios';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import {
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherResource
} from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import Logger, { LoggingModule } from 'services/Logger';
import queryString from 'querystring';
import { RootDispatch, Thunk } from 'store/types';
import { dfdAccessTokenSelector } from './selectors';
import {
  fetchTPSTokenForSource,
  checkAuthSources,
  TpsStatus,
  formatAuthForStorage
} from './tpsHelpers';
import { AuthLiteRequest, LoginType, UserSession } from './types';
import { MHP_AUTH_CONFIG } from './configs.common';
import { AuthResult } from './reducers';
import { authenticateUserFail } from './actions';

export const SET_TPS_AUTH_STATUS = 'AUTH.SET_TPS_AUTH_STATUS';
export const setAuthStatusTPS = (statuses: TpsStatus[]) => ({
  type: SET_TPS_AUTH_STATUS,
  payload: statuses
});

export const SET_TPS_AUTH_STATUS_FETCHING = 'AUTH.SET_TPS_AUTH_STATUS_FETCHING';
export const setAuthStatusFetchingTPS = (fetching: boolean) => ({
  type: SET_TPS_AUTH_STATUS_FETCHING,
  payload: fetching
});

export const SET_TPS_AUTH_STATUS_ERROR = 'AUTH.SET_TPS_AUTH_STATUS_ERROR';
export const setAuthStatusErrorTPS = (error: Error | null) => ({
  type: SET_TPS_AUTH_STATUS_ERROR,
  payload: error
});

export const SET_LOGIN_TYPE = 'AUTH.SET_LOGIN_TYPE';
export const setLoginType = (type: LoginType) => ({
  type: SET_LOGIN_TYPE,
  payload: type
});

export const FETCH_TPS_TOKEN = 'AUTH.FETCH_TPS_TOKEN';
export const FETCH_TPS_TOKEN_SUCCESS = 'AUTH.FETCH_TPS_TOKEN_SUCCESS';
export const FETCH_TPS_TOKEN_FAIL = 'AUTH.FETCH_TPS_TOKEN_FAIL';

export const fetchTokenForSource = (source: string): Thunk => async (dispatch, getState) => {
  const dfdToken = dfdAccessTokenSelector(getState());
  const apiKey = apiKeySelector(getState());
  dispatch({
    type: FETCH_TPS_TOKEN,
    payload: {
      source
    }
  });

  if (!dfdToken || !apiKey) {
    dispatch({
      type: FETCH_TPS_TOKEN_FAIL,
      payload: {
        source
      },
      error: new Error('No tokens found')
    });
  }

  try {
    const tokenResponse = await fetchTPSTokenForSource(source, { token: dfdToken, apiKey });

    dispatch({
      type: FETCH_TPS_TOKEN_SUCCESS,
      payload: {
        source,
        data: tokenResponse
      }
    });
  } catch (error) {
    dispatch({
      type: FETCH_TPS_TOKEN_FAIL,
      payload: { source },
      error
    });
  }
};

export const getTPSAuthStatus = (): Thunk => async (dispatch, getState) => {
  try {
    const state = getState();
    const apiKey = apiKeySelector(state);
    const token = dfdAccessTokenSelector(state);
    if (token && apiKey) {
      dispatch(setAuthStatusFetchingTPS(true));
      const results = await checkAuthSources({ token, apiKey });
      dispatch(setAuthStatusTPS(results));
      return dispatch(setAuthStatusFetchingTPS(false));
    }
    throw new Error('No token available');
  } catch (err) {
    return dispatch(authenticateUserFail((error as Error).message));
  }
};

// web only
export const HIDE_GUEST_BENEFITS_MODAL = 'AUTH.HIDE_GUEST_BENEFITS_MODAL';
export const hideGuestBenefitsModal = () => ({
  type: HIDE_GUEST_BENEFITS_MODAL
});

export const TOGGLE_PROTECTED_ROUTE_MODAL = 'AUTH.TOGGLE_PROTECTED_ROUTE_MODAL';
export const toggleProtectedRouteModal = () => ({
  type: TOGGLE_PROTECTED_ROUTE_MODAL
});

export const AUTH_LITE_LOGIN = 'AUTH.AUTH_LITE_LOGIN';
export const AUTH_LITE_LOGIN_FAIL = 'AUTH.AUTH_LITE_LOGIN_FAIL';
export const AUTH_LITE_LOGIN_SUCCESS = 'AUTH.AUTH_LITE_LOGIN_SUCCESS';

export const authLiteLogin = (data: AuthLiteRequest) => ({
  type: AUTH_LITE_LOGIN,
  payload: {
    client: 'tps',
    request: {
      method: 'post',
      url: '/authlite.jwt',
      data
    }
  }
});

export const AUTH_OPEN = 'AUTH.AUTH_OPEN';
export const BIOMETRICS_OPEN = 'AUTH.BIOMETRICS_OPEN';

export const AUTHENTICATE_USER = 'AUTH.AUTHENTICATE_USER';
export const AUTHENTICATE_USER_SUCCESS = 'AUTH.AUTHENTICATE_USER_SUCCESS';
export const AUTHENTICATE_USER_FAIL = 'AUTH.AUTHENTICATE_USER_FAIL';

const createRedirectUrl = (path: string) => `${window?.location?.origin || ''}${path}`;
export const getToken = (code: string) => async (dispatch: RootDispatch) => {
  dispatch({ type: AUTHENTICATE_USER });
  const isMobile = Config.AUTH_REDIRECT_URL.includes('dfdmobile');

  try {
    const { tokenEndpoint, client_id, grant_type, clientSecret } = MHP_AUTH_CONFIG;
    if (!code) {
      throw new Error('No code provided');
    }

    const body = isMobile
      ? {
          client_id,
          grant_type,
          code,
          redirect_uri: Config.AUTH_REDIRECT_URL,
          client_secret: clientSecret,
          scope: MHP_AUTH_CONFIG.scope
        }
      : {
          client_id,
          grant_type,
          code,
          redirect_uri: createRedirectUrl(Config.AUTH_REDIRECT_URL),
          client_secret: clientSecret
        };
    const response = await axios.post(tokenEndpoint, queryString.stringify(body), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
      }
    });

    if (!response || response.status !== 200) {
      throw new Error('Bad token response');
    }
    const dfdAuth = (formatAuthForStorage(response.data) as unknown) as AuthResult;
    dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: { dfdAuth } });
    return dfdAuth;
  } catch (error) {
    let errorPayload = '';
    try {
      errorPayload = JSON.stringify(error);
    } catch (err) {
      errorPayload = String(error);
    }
    if (errorPayload.length > 4000) {
      errorPayload = errorPayload.substring(0, 3999);
    }
    Logger.error(`${LoggingModule.authService}FAIL getAuthToken()`, {
      message: 'Failed to create Redirect URL',
      errorMessage: error.message,
      errorPayload
    });
    const err = error.message ? error : new Error('Failed to create Redirect URL');
    dispatch({ type: AUTHENTICATE_USER_FAIL, error: err });
    throw err;
  }
};

export const AUTH_VERIFY_TOKEN = 'AUTH.AUTH_VERIFY_TOKEN';
export const AUTH_VERIFY_TOKEN_FAIL = 'AUTH.AUTH_VERIFY_TOKEN_FAIL';
export const AUTH_VERIFY_TOKEN_SUCCESS = 'AUTH.AUTH_VERIFY_TOKEN_SUCCESS';

export const verifyToken = (token: string) => ({
  type: AUTH_VERIFY_TOKEN,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        '',
        DispatcherAuthPrefix.PUBLIC,
        DispatcherResource.TOKEN,
        '/verification'
      ),
      headers: {
        [DFDHeaders.authorization]: `Bearer ${token}`
      }
    }
  }
});

export const setUserSession = (tokens: AuthResult, apikey: string) => {
  const headers = {
    apikey,
    Authorization: `BEARER ${tokens?.accessToken}`,
    [DFDHeaders.mhpAdapter]: 'mhp'
  };

  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.USER_SESSION,
    '',
    true
  );
  return axios.post(
    url,
    { payload: tokens },
    {
      headers
    }
  );
};

export const getUserSession = (token: string, apikey: string): Promise<UserSession> => {
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.USER_SESSION,
    '',
    true
  );
  const config = {
    headers: { apikey, Authorization: `BEARER ${token}`, 'mhp-adapter': 'mhp' }
  };
  return axios.get(url, config).then(res => res?.data?.data);
};
